import { Wishlist } from '@theme/wishlist-store';

/**
 * Wishlist controller — one delegated listener for every heart on the page.
 *
 * Progressive enhancement (the plan's K1): localStorage is the always-on layer. Guests use
 * it exclusively. When the signed-in sync is live (`data-endpoint` present on the feedback
 * singleton) a logged-in shopper's server metafield is the truth set and toggles also POST
 * to the App Proxy; a guest's local list merges into the account on the first authenticated
 * load. Until the endpoint is configured, logged-in shoppers use the local layer too.
 *
 * Zero per-card element: a single capture-phase click listener handles every `[data-wishlist-toggle]`
 * button, and a MutationObserver paints hearts that arrive after load (PDP recommendations,
 * predictive search, Section-Rendering morphs). Feedback (announcements + error toast) rides
 * the single `#wishlist-feedback` surface, never a per-card region.
 */

const TOGGLE_SELECTOR = '[data-wishlist-toggle]';
const REQUEST_TIMEOUT_MS = 8000;
const TOAST_DURATION_MS = 5000;

/** @returns {HTMLElement | null} */
const feedbackEl = () => document.getElementById('wishlist-feedback');

/** Config inlined once on the feedback singleton by snippets/wishlist-feedback.liquid. */
function readConfig() {
  const el = feedbackEl();
  const loggedIn = el?.dataset.loggedIn === 'true';
  const endpoint = el?.dataset.endpoint || '';
  let serverIds = [];
  if (loggedIn && endpoint && el?.dataset.serverIds) {
    try {
      const parsed = JSON.parse(el.dataset.serverIds);
      // wishlist_ids is a `{ "<id>": 1 }` map; its keys are the saved product ids.
      serverIds = parsed && typeof parsed === 'object' ? Object.keys(parsed) : [];
    } catch {
      serverIds = [];
    }
  }
  return { loggedIn, endpoint, serverIds, syncEnabled: Boolean(loggedIn && endpoint) };
}

const config = readConfig();

/**
 * The truth set for painting. Signed-in + sync-on → the server set; everyone else → the
 * local store. A live Set so optimistic toggles reflect immediately.
 * @type {Set<string>}
 */
const saved = new Set(config.syncEnabled ? config.serverIds : Wishlist.all());

/* ---- localization (read off inlined labels; fall back to English) ---- */
const strings = (() => {
  const el = feedbackEl();
  return {
    announceSaved: el?.dataset.announceSaved || 'Saved to wishlist',
    announceRemoved: el?.dataset.announceRemoved || 'Removed from wishlist',
    errorFull: el?.dataset.errorFull || 'Your wishlist is full — you can save up to 128 items.',
    errorAuth: el?.dataset.errorAuth || 'Please sign in again to save this.',
    errorFailed: el?.dataset.errorFailed || 'Something went wrong — tap to try again.',
  };
})();

/* ---------------- feedback surface ---------------- */

let toastTimer;

/** @param {string} message */
function announce(message) {
  const region = feedbackEl()?.querySelector('[data-wishlist-announce]');
  if (region) region.textContent = message;
}

/** @param {string} message */
function toast(message) {
  const el = feedbackEl();
  const toastEl = el?.querySelector('[data-wishlist-toast]');
  const textEl = el?.querySelector('[data-wishlist-toast-text]');
  if (!(toastEl instanceof HTMLElement) || !textEl) return;
  textEl.textContent = message;
  toastEl.hidden = false;
  // Force a frame so the transition runs from the hidden resting state.
  requestAnimationFrame(() => toastEl.setAttribute('data-visible', ''));
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastEl.removeAttribute('data-visible');
    toastEl.hidden = true;
  }, TOAST_DURATION_MS);
}

/* ---------------- painting ---------------- */

/**
 * @param {Element} button
 * @param {boolean} isSaved
 */
function paintButton(button, isSaved) {
  button.toggleAttribute('data-saved', isSaved);
  button.setAttribute('aria-pressed', String(isSaved));
}

/** @param {ParentNode} [root] */
function paint(root = document) {
  root.querySelectorAll(TOGGLE_SELECTOR).forEach((button) => {
    const id = button.getAttribute('data-product-id');
    if (id) paintButton(button, saved.has(id));
  });
}

/* ---------------- analytics ---------------- */

/**
 * @param {HTMLElement} button
 * @param {'add' | 'remove'} action
 */
function publishAnalytics(button, action) {
  const priceCents = Number(button.dataset.itemPrice);
  window.Shopify?.analytics?.publish('bhaavyas_wishlist', {
    action,
    item_id: button.dataset.itemId,
    item_name: button.dataset.itemName,
    price: Number.isFinite(priceCents) ? priceCents / 100 : undefined,
    currency: 'INR',
    value: Number.isFinite(priceCents) ? priceCents / 100 : undefined,
  });
}

/* ---------------- write client (Phase 2 — App Proxy) ---------------- */

/**
 * POST to the App Proxy write route. Same-origin relative URL: Shopify signs the request and
 * injects the customer id server-side, so the theme sends no auth and no customer id — only
 * the operation and product id(s). Errors key off HTTP status, never the body string.
 * @param {object} body
 * @returns {Promise<{ok: boolean, status: number}>}
 */
async function postWrite(body) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return { ok: response.ok, status: response.status };
  } catch {
    return { ok: false, status: 0 };
  } finally {
    clearTimeout(timer);
  }
}

/** @param {number} status */
function toastForStatus(status) {
  if (status === 401) return strings.errorAuth;
  if (status === 409) return strings.errorFull;
  return strings.errorFailed;
}

/* ---------------- toggle ---------------- */

/** Do-not-write surfaces: Shopify injects no customer id on the order-status page → writes 401. */
function writesSuppressed() {
  return /\/(orders|thank[_-]?you)\//.test(window.location.pathname);
}

/** @param {HTMLElement} button */
async function onToggle(button) {
  const id = button.dataset.productId;
  if (!id || button.hasAttribute('data-busy')) return;

  const wasSaved = saved.has(id);
  const nextSaved = !wasSaved;
  const action = nextSaved ? 'add' : 'remove';

  // Optimistic flip.
  if (nextSaved) saved.add(id);
  else saved.delete(id);
  paintButton(button, nextSaved);
  button.setAttribute('data-just-toggled', '');
  window.setTimeout(() => button.removeAttribute('data-just-toggled'), 200);

  const revert = () => {
    if (wasSaved) saved.add(id);
    else saved.delete(id);
    paint(); // repaint every instance of this product's heart
  };

  if (config.syncEnabled && !writesSuppressed()) {
    button.setAttribute('data-busy', '');
    const { ok, status } = await postWrite({ op: action, productId: id });
    button.removeAttribute('data-busy');
    if (!ok) {
      revert();
      toast(toastForStatus(status));
      return;
    }
  } else {
    // Local layer (guests, and logged-in shoppers until sync is live).
    const result = Wishlist.toggle(id);
    if (result === 'error' || result === 'full') {
      revert();
      toast(result === 'full' ? strings.errorFull : strings.errorFailed);
      return;
    }
  }

  announce(nextSaved ? strings.announceSaved : strings.announceRemoved);
  publishAnalytics(button, action);

  // On the wishlist page, a remove drops the card optimistically and keeps focus in place.
  if (action === 'remove') removeFromWishlistPage(button);
}

/**
 * Wishlist-page removal (U10): drop the removed item's card without a reload and move focus
 * to the next remaining card's heart — or the page heading when the list empties — so keyboard
 * and screen-reader users keep their place.
 * @param {HTMLElement} button
 */
function removeFromWishlistPage(button) {
  const grid = button.closest('[data-wishlist-page-grid]');
  if (!grid) return;
  const item = button.closest('.products-grid__item') || button.closest('.product-card');
  if (!item) return;

  const hearts = Array.from(grid.querySelectorAll(TOGGLE_SELECTOR));
  const index = hearts.indexOf(button);
  const nextHeart = hearts[index + 1] || hearts[index - 1] || null;

  item.remove();

  if (nextHeart instanceof HTMLElement) {
    nextHeart.focus();
  } else {
    // List emptied — move focus to the page heading so focus doesn't fall to <body>.
    const heading = document.querySelector('.wishlist-page__title');
    if (heading instanceof HTMLElement) {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    }
  }
}

/* ---------------- merge on login ---------------- */

/**
 * On the first authenticated load with local saves, union local + server ids and post a
 * merge (union-only, idempotent), then clear local. If the union exceeds the cap the backend
 * rejects the whole merge (409) — keep local intact so nothing is lost.
 */
async function mergeOnLogin() {
  if (!config.syncEnabled || writesSuppressed()) return;
  const local = Wishlist.all();
  if (local.length === 0) return;

  const union = Array.from(new Set([...config.serverIds, ...local]));
  const { ok, status } = await postWrite({ op: 'merge', productIds: union });
  if (ok) {
    Wishlist.clear();
    union.forEach((id) => saved.add(id));
    paint();
    // The wishlist page grid is server-rendered from the metafield, so the items we just merged
    // won't appear until the next render. Reload once to surface them. localStorage is now cleared,
    // so the next load performs no merge and won't reload again (no loop). Other surfaces don't
    // reload — their hearts already reflect the merged set via paint().
    if (document.querySelector('.wishlist-page')) window.location.reload();
  } else if (status === 409) {
    toast(strings.errorFull);
  }
  // Other failures: keep local; the next authenticated load retries (merge is idempotent).
}

/* ---------------- wiring ---------------- */

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest(TOGGLE_SELECTOR);
    if (button instanceof HTMLElement) onToggle(button);
  },
  true
);

// Paint hearts that arrive after initial load (recommendations, predictive search, morphs).
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.(TOGGLE_SELECTOR)) paintButton(node, saved.has(node.getAttribute('data-product-id') || ''));
      else if (node.querySelector?.(TOGGLE_SELECTOR)) paint(node);
    }
  }
});

/**
 * On the guest wishlist page, upgrade the default "nothing saved" prompt to the counted
 * message when the device has saved items. No-op when signed in or the block is absent.
 */
function fillGuestCount() {
  const guest = document.querySelector('[data-wishlist-guest]');
  if (!guest || config.loggedIn) return;
  const n = Wishlist.count();
  if (n <= 0) return;
  const body = guest.querySelector('[data-wishlist-guest-body]');
  const prompt = guest.querySelector('[data-wishlist-guest-prompt]');
  if (body) body.textContent = (guest.dataset.countTemplate || '').replace('__N__', String(n));
  if (prompt) prompt.textContent = guest.dataset.prompt || prompt.textContent;
}

function init() {
  paint();
  fillGuestCount();
  document.documentElement.classList.add('wishlist-ready');
  observer.observe(document.body, { childList: true, subtree: true });
  mergeOnLogin();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
